/**
 * Sichere Formel-Engine für berechnete Zusatzfelder.
 *
 * Ausdrücke werden zu einem Syntaxbaum geparst und anschließend über die
 * exakte Dezimalarithmetik ausgewertet — niemals über `eval` oder `Function`.
 * Web und API teilen sich diese Engine, damit die Live-Vorschau im Beleg und
 * der gespeicherte Wert identisch sind.
 */
import { addDecimal, compareDecimal, divideDecimal, multiplyDecimal, negateDecimal, roundDecimal, subtractDecimal, } from './decimal.js';
/** Rechengenauigkeit während der Auswertung; gerundet wird erst am Ende. */
const WORKING_SCALE = 10;
export const FORMULA_MAX_LENGTH = 500;
export const FORMULA_MAX_TOKENS = 200;
export const FORMULA_MAX_DEPTH = 32;
export const FORMULA_DECIMALS_MIN = 0;
export const FORMULA_DECIMALS_MAX = 6;
/** Kennzahlen der Positionszeile, die in jeder Formel zur Verfügung stehen. */
export const FORMULA_LINE_VARIABLES = [
    'quantity',
    'unitPrice',
    'discountPercent',
    'taxRate',
    'lineNet',
    'lineGross',
    'lineTotal',
];
/** Erlaubte Präfixe für Zusatzfeld-Verweise; der Schlüssel dahinter ist frei. */
export const FORMULA_VARIABLE_PREFIXES = [
    'item.customFields.',
    'product.customFields.',
    'document.customFields.',
    'customer.customFields.',
    'spec.',
];
export const FORMULA_FUNCTIONS = ['abs', 'min', 'max', 'round', 'floor', 'ceil'];
export class FormulaError extends Error {
    constructor(message) {
        super(message);
        this.name = 'FormulaError';
    }
}
const NUMBER_START = /[0-9.]/u;
const IDENTIFIER_START = /[A-Za-z_]/u;
const IDENTIFIER_PART = /[A-Za-z0-9_.]/u;
function tokenize(expression) {
    const tokens = [];
    let index = 0;
    while (index < expression.length) {
        const char = expression.charAt(index);
        if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
            index += 1;
            continue;
        }
        if (char === '(' || char === ')') {
            tokens.push({ type: 'paren', value: char, position: index });
            index += 1;
        }
        else if (char === ',') {
            tokens.push({ type: 'comma', value: char, position: index });
            index += 1;
        }
        else if (char === '|') {
            if (expression.charAt(index + 1) !== '|') {
                throw new FormulaError(`Unerwartetes Zeichen "|" an Position ${String(index + 1)}. Für den Ersatzwert wird "||" geschrieben.`);
            }
            tokens.push({ type: 'operator', value: '||', position: index });
            index += 2;
        }
        else if (char === '+' || char === '-' || char === '*' || char === '/') {
            tokens.push({ type: 'operator', value: char, position: index });
            index += 1;
        }
        else if (NUMBER_START.test(char)) {
            const start = index;
            let seenDot = false;
            while (index < expression.length) {
                const current = expression.charAt(index);
                if (current === '.') {
                    if (seenDot) {
                        break;
                    }
                    seenDot = true;
                }
                else if (!/[0-9]/u.test(current)) {
                    break;
                }
                index += 1;
            }
            const raw = expression.slice(start, index);
            if (!/^\d+(\.\d+)?$|^\.\d+$/u.test(raw)) {
                throw new FormulaError(`"${raw}" ist keine gültige Zahl.`);
            }
            tokens.push({
                type: 'number',
                value: raw.startsWith('.') ? `0${raw}` : raw,
                position: start,
            });
        }
        else if (IDENTIFIER_START.test(char)) {
            const start = index;
            while (index < expression.length && IDENTIFIER_PART.test(expression.charAt(index))) {
                index += 1;
            }
            tokens.push({ type: 'identifier', value: expression.slice(start, index), position: start });
        }
        else {
            throw new FormulaError(`Unerlaubtes Zeichen "${char}" an Position ${String(index + 1)}. Zulässig sind Zahlen, Variablen, Klammern und die Rechenzeichen + - * / ||.`);
        }
        if (tokens.length > FORMULA_MAX_TOKENS) {
            throw new FormulaError(`Die Formel ist zu komplex (maximal ${String(FORMULA_MAX_TOKENS)} Bestandteile).`);
        }
    }
    return tokens;
}
// ---------------------------------------------------------------------------
// Zerteilung
// ---------------------------------------------------------------------------
function isFunctionName(value) {
    return FORMULA_FUNCTIONS.includes(value);
}
/** Prüft den Verweis gegen die bekannten Kennzahlen und Zusatzfeld-Präfixe. */
export function isKnownFormulaVariable(path) {
    if (FORMULA_LINE_VARIABLES.includes(path)) {
        return true;
    }
    return FORMULA_VARIABLE_PREFIXES.some((prefix) => path.startsWith(prefix) && path.length > prefix.length);
}
class Parser {
    tokens;
    index = 0;
    depth = 0;
    constructor(tokens) {
        this.tokens = tokens;
    }
    parse() {
        const node = this.parseFallback();
        const rest = this.tokens[this.index];
        if (rest !== undefined) {
            throw new FormulaError(`Unerwartetes "${rest.value}" an Position ${String(rest.position + 1)}.`);
        }
        return node;
    }
    peek() {
        return this.tokens[this.index];
    }
    enter() {
        this.depth += 1;
        if (this.depth > FORMULA_MAX_DEPTH) {
            throw new FormulaError('Die Formel ist zu tief verschachtelt.');
        }
    }
    leave() {
        this.depth -= 1;
    }
    parseFallback() {
        let left = this.parseAdditive();
        for (;;) {
            const token = this.peek();
            if (token === undefined || token.type !== 'operator' || token.value !== '||') {
                return left;
            }
            this.index += 1;
            left = { kind: 'fallback', left, right: this.parseAdditive() };
        }
    }
    parseAdditive() {
        let left = this.parseMultiplicative();
        for (;;) {
            const token = this.peek();
            if (token === undefined ||
                token.type !== 'operator' ||
                (token.value !== '+' && token.value !== '-')) {
                return left;
            }
            this.index += 1;
            left = {
                kind: 'binary',
                operator: token.value,
                left,
                right: this.parseMultiplicative(),
            };
        }
    }
    parseMultiplicative() {
        let left = this.parseUnary();
        for (;;) {
            const token = this.peek();
            if (token === undefined ||
                token.type !== 'operator' ||
                (token.value !== '*' && token.value !== '/')) {
                return left;
            }
            this.index += 1;
            left = { kind: 'binary', operator: token.value, left, right: this.parseUnary() };
        }
    }
    parseUnary() {
        const token = this.peek();
        if (token !== undefined && token.type === 'operator' && token.value === '-') {
            this.index += 1;
            return { kind: 'negate', operand: this.parseUnary() };
        }
        if (token !== undefined && token.type === 'operator' && token.value === '+') {
            this.index += 1;
            return this.parseUnary();
        }
        return this.parsePrimary();
    }
    parsePrimary() {
        const token = this.peek();
        if (token === undefined) {
            throw new FormulaError('Die Formel endet unvollständig.');
        }
        if (token.type === 'number') {
            this.index += 1;
            return { kind: 'number', value: token.value };
        }
        if (token.type === 'paren' && token.value === '(') {
            this.enter();
            this.index += 1;
            const inner = this.parseFallback();
            this.expectClosingParen();
            this.leave();
            return inner;
        }
        if (token.type === 'identifier') {
            this.index += 1;
            const next = this.peek();
            if (next !== undefined && next.type === 'paren' && next.value === '(') {
                if (!isFunctionName(token.value)) {
                    throw new FormulaError(`Die Funktion "${token.value}" ist nicht verfügbar. Erlaubt sind: ${FORMULA_FUNCTIONS.join(', ')}.`);
                }
                this.enter();
                this.index += 1;
                const args = this.parseArguments();
                this.leave();
                return { kind: 'call', name: token.value, args };
            }
            if (!isKnownFormulaVariable(token.value)) {
                throw new FormulaError(`Die Variable "${token.value}" ist unbekannt. Zulässig sind ${FORMULA_LINE_VARIABLES.join(', ')} sowie Zusatzfelder über ${FORMULA_VARIABLE_PREFIXES.join(', ')}.`);
            }
            return { kind: 'variable', path: token.value };
        }
        throw new FormulaError(`Unerwartetes "${token.value}" an Position ${String(token.position + 1)}.`);
    }
    parseArguments() {
        const args = [];
        const first = this.peek();
        if (first !== undefined && first.type === 'paren' && first.value === ')') {
            this.index += 1;
            return args;
        }
        for (;;) {
            args.push(this.parseFallback());
            const token = this.peek();
            if (token !== undefined && token.type === 'comma') {
                this.index += 1;
                continue;
            }
            this.expectClosingParen();
            return args;
        }
    }
    expectClosingParen() {
        const token = this.peek();
        if (token === undefined || token.type !== 'paren' || token.value !== ')') {
            throw new FormulaError('Es fehlt eine schließende Klammer.');
        }
        this.index += 1;
    }
}
/** Zerlegt den Ausdruck und wirft bei jedem Syntax- oder Namensfehler. */
export function parseFormula(expression) {
    const trimmed = expression.trim();
    if (trimmed.length === 0) {
        throw new FormulaError('Die Formel darf nicht leer sein.');
    }
    if (trimmed.length > FORMULA_MAX_LENGTH) {
        throw new FormulaError(`Die Formel darf höchstens ${String(FORMULA_MAX_LENGTH)} Zeichen lang sein.`);
    }
    const tokens = tokenize(trimmed);
    if (tokens.length === 0) {
        throw new FormulaError('Die Formel darf nicht leer sein.');
    }
    return new Parser(tokens).parse();
}
/** Sammelt alle referenzierten Variablenpfade, z. B. für den Formeleditor. */
export function collectFormulaVariables(node) {
    const paths = new Set();
    const walk = (current) => {
        switch (current.kind) {
            case 'variable':
                paths.add(current.path);
                return;
            case 'negate':
                walk(current.operand);
                return;
            case 'binary':
            case 'fallback':
                walk(current.left);
                walk(current.right);
                return;
            case 'call':
                for (const argument of current.args) {
                    walk(argument);
                }
                return;
            default:
        }
    };
    walk(node);
    return [...paths];
}
// ---------------------------------------------------------------------------
// Auswertung
// ---------------------------------------------------------------------------
const NUMERIC_PATTERN = /^-?\d+(\.\d+)?$/;
function readVariable(context, path) {
    const raw = Object.prototype.hasOwnProperty.call(context, path) ? context[path] : null;
    if (raw === null || raw === undefined) {
        return null;
    }
    const normalized = raw.trim().replace(',', '.');
    return NUMERIC_PATTERN.test(normalized) ? normalized : null;
}
function truncateToInteger(value, mode) {
    const rounded = roundDecimal(value, 0);
    const comparison = compareDecimal(rounded, value);
    if (comparison === 0) {
        return rounded;
    }
    if (mode === 'floor' && comparison === 1) {
        return subtractDecimal(rounded, '1', 0);
    }
    if (mode === 'ceil' && comparison === -1) {
        return addDecimal(rounded, '1', 0);
    }
    return rounded;
}
function applyFunction(name, args) {
    if (args.some((argument) => argument === null)) {
        return null;
    }
    const values = args;
    const [first] = values;
    if (first === undefined) {
        return null;
    }
    switch (name) {
        case 'abs':
            return compareDecimal(first, '0') === -1 ? negateDecimal(first) : first;
        case 'min':
            return values.reduce((best, value) => (compareDecimal(value, best) === -1 ? value : best));
        case 'max':
            return values.reduce((best, value) => (compareDecimal(value, best) === 1 ? value : best));
        case 'round': {
            const scale = values[1];
            const decimals = scale === undefined ? 0 : Number(roundDecimal(scale, 0));
            if (!Number.isInteger(decimals) || decimals < 0 || decimals > WORKING_SCALE) {
                return null;
            }
            return roundDecimal(first, decimals);
        }
        case 'floor':
            return truncateToInteger(first, 'floor');
        case 'ceil':
            return truncateToInteger(first, 'ceil');
        default:
            return null;
    }
}
function evaluateNode(node, context) {
    switch (node.kind) {
        case 'number':
            return node.value;
        case 'variable':
            return readVariable(context, node.path);
        case 'negate': {
            const operand = evaluateNode(node.operand, context);
            return operand === null ? null : negateDecimal(operand);
        }
        case 'fallback': {
            const left = evaluateNode(node.left, context);
            if (left !== null && compareDecimal(left, '0') !== 0) {
                return left;
            }
            return evaluateNode(node.right, context);
        }
        case 'binary': {
            const left = evaluateNode(node.left, context);
            const right = evaluateNode(node.right, context);
            if (left === null || right === null) {
                return null;
            }
            switch (node.operator) {
                case '+':
                    return addDecimal(left, right, WORKING_SCALE);
                case '-':
                    return subtractDecimal(left, right, WORKING_SCALE);
                case '*':
                    return multiplyDecimal(left, right, WORKING_SCALE);
                case '/':
                    // Eine Division durch null liefert keinen Wert statt eines Fehlers,
                    // damit eine unvollständig gepflegte Position speicherbar bleibt.
                    return compareDecimal(right, '0') === 0
                        ? null
                        : divideDecimal(left, right, WORKING_SCALE);
                default:
                    return null;
            }
        }
        case 'call':
            return applyFunction(node.name, node.args.map((argument) => evaluateNode(argument, context)));
        default:
            return null;
    }
}
/**
 * Wertet einen bereits geparsten Ausdruck aus. `null` bedeutet, dass ein
 * benötigter Wert fehlt oder durch null geteilt würde.
 */
export function evaluateFormulaNode(node, context, decimals) {
    const result = evaluateNode(node, context);
    if (result === null) {
        return null;
    }
    const scale = Math.min(Math.max(Math.trunc(decimals), FORMULA_DECIMALS_MIN), FORMULA_DECIMALS_MAX);
    return roundDecimal(result, scale);
}
/** Bequemlichkeitshülle für einmalige Auswertungen; wirft bei Syntaxfehlern. */
export function evaluateFormula(expression, context, decimals) {
    return evaluateFormulaNode(parseFormula(expression), context, decimals);
}
/** Anzeigeform eines Formelergebnisses inklusive Einheit, z. B. „0,42 €/Stk". */
export function formatFormulaValue(value, options) {
    if (value === null) {
        return '—';
    }
    const decimals = Math.min(Math.max(Math.trunc(options.decimals), FORMULA_DECIMALS_MIN), FORMULA_DECIMALS_MAX);
    const formatted = new Intl.NumberFormat(options.locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(Number(value));
    const unit = options.unit ?? null;
    return unit === null || unit.length === 0 ? formatted : `${formatted} ${unit}`;
}
//# sourceMappingURL=formula.js.map